// Shared JSON API helper for Vue components, Stimulus controllers, and plain JS.
// Centralizes CSRF token lookup, default headers, body serialization, and error
// normalization so call sites don't each reimplement them.
//
// Vue:      const api = useApi()
// Stimulus: import { api } from '../composables/useApi'
//
//   const data = await api.get('/friendships', { params: { page: 1 } })
//   await api.post('/friendships', { handle: 'someone' })
//
// Non-2xx responses throw ApiError; network failures throw TypeError (from
// fetch). Use apiErrorMessage(e) to surface a server-provided message with a
// generic fallback.

export class ApiError extends Error {
  constructor(status, data) {
    const serverMessage =
      data?.error || (Array.isArray(data?.errors) ? data.errors.join(', ') : null)
    super(serverMessage || `Request failed (${status})`)
    this.name = 'ApiError'
    this.status = status
    this.data = data
    this.serverMessage = serverMessage || null
  }
}

export function apiErrorMessage(error, fallback = 'Something went wrong') {
  return (error instanceof ApiError && error.serverMessage) || fallback
}

// Read at request time, not import time: Turbo can swap the <head> token.
export function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

export async function apiFetch(url, { method = 'GET', body, params, headers = {} } = {}) {
  const requestHeaders = { Accept: 'application/json', ...headers }

  if (method !== 'GET') {
    const token = csrfToken()
    if (token) requestHeaders['X-CSRF-Token'] = token
  }

  let requestBody = body
  if (body !== undefined && !(body instanceof FormData)) {
    requestHeaders['Content-Type'] = 'application/json'
    requestBody = JSON.stringify(body)
  }

  let requestUrl = url
  if (params) {
    const query = new URLSearchParams(params).toString()
    if (query) requestUrl += (requestUrl.includes('?') ? '&' : '?') + query
  }

  const res = await fetch(requestUrl, { method, headers: requestHeaders, body: requestBody })

  let data = null
  if (res.status !== 204) {
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('json')) data = await res.json().catch(() => null)
  }

  if (!res.ok) throw new ApiError(res.status, data)
  return data
}

export const api = {
  get: (url, options = {}) => apiFetch(url, options),
  post: (url, body, options = {}) => apiFetch(url, { ...options, method: 'POST', body }),
  patch: (url, body, options = {}) => apiFetch(url, { ...options, method: 'PATCH', body }),
  put: (url, body, options = {}) => apiFetch(url, { ...options, method: 'PUT', body }),
  delete: (url, options = {}) => apiFetch(url, { ...options, method: 'DELETE' }),
}

export function useApi() {
  return api
}
